import axiosInstance from './axios/axiosConfig'
import { endpoints } from './axios/endpoints'

// Every assistant call shows its own state (the Guide's loading and error,
// the panel's "looking this up" and failures), so none raises a loader or a
// toast.
const QUIET = { background: true, silentToast: true, silentErrorToast: true }

// An answer takes 5 to 30 seconds (p50 7.5 s, p95 11 s on dev) and the
// service bounds each model call under the ingress's 60 s; past that the
// request has no answer coming.
export const ASK_TIMEOUT_MS = 65000

// The guide as last downloaded this session: {markdown, version, etag}.
let guideCopy = null

/** Forget the downloaded guide (tests; a session starts with none). */
export const resetGuideCopy = () => {
    guideCopy = null
}

const AssistantService = {
    /** The guide this session already has, or null: the page shows it at once while it checks for a newer one. */
    cachedGuide: () => guideCopy,

    /**
     * The Comms Guide (GET /comms/v2/assistant/guide): `{markdown, version}`.
     *
     * The copy is kept for the session and sent back as If-None-Match, so a
     * 304 (the guide has not changed) reuses it without downloading ~100 KB
     * again. The ETag is the version in quotes; when the browser does not
     * expose the header to the page, it is rebuilt from the version.
     *
     * @returns {Promise<{markdown: string, version: string, etag: string}>}
     */
    guide: async () => {
        const resp = await axiosInstance.get(endpoints.assistantGuide, {
            ...QUIET,
            headers: guideCopy ? { 'If-None-Match': guideCopy.etag } : {},
            validateStatus: (status) =>
                (status >= 200 && status < 300) || status === 304,
        })
        if (resp.status === 304 && guideCopy) return guideCopy
        const { markdown, version } = resp.data ?? {}
        if (typeof markdown !== 'string')
            throw new Error('The guide answer has no text')
        guideCopy = {
            markdown,
            version: version ?? '',
            etag: resp.headers?.etag ?? `"${version ?? ''}"`,
        }
        return guideCopy
    },

    /** The signed-in person's conversations, 20 a page, newest activity first. */
    listConversations: (cursor) =>
        axiosInstance.get(endpoints.assistantConversations, {
            ...QUIET,
            params: cursor ? { cursor } : {},
        }),

    /** One conversation and its messages, oldest first (404 when it is not this person's). */
    getConversation: (conversationID) =>
        axiosInstance.get(
            endpoints.assistantConversation(conversationID),
            QUIET
        ),

    /** Start a conversation with its first question: `{message, page: {path, title}}`. */
    start: ({ message, page }) =>
        axiosInstance.post(
            endpoints.assistantConversations,
            { message, page },
            { ...QUIET, timeout: ASK_TIMEOUT_MS }
        ),

    /**
     * Ask in a conversation, or (with `retryOf`, a question id) ask again
     * for the answer that failed; the service reads the question itself.
     */
    send: (conversationID, { message, page, retryOf }) =>
        axiosInstance.post(
            endpoints.assistantMessages(conversationID),
            retryOf ? { retry_of: retryOf, page } : { message, page },
            { ...QUIET, timeout: ASK_TIMEOUT_MS }
        ),
}

export default AssistantService
